import type { ResourceId, Square } from "src/api";

import type { World } from "../../../../render/world/World";
import { WorldObject } from "../../../../render/world/WorldObject";
import type { BoardUpdate } from "../../updates/BoardUpdate";
import type { BoardWorldObject } from "../../updates/BoardWorldObject";
import { ResourceDeposit } from "./ResourceDeposit";

export class BoardResourceDeposits
  extends WorldObject
  implements BoardWorldObject
{
  private readonly resourceDepositBySquare = new Map<Square, ResourceDeposit>();
  private readonly world: World;

  constructor(world: World) {
    super();

    this.world = world;
  }

  onBoardUpdate(boardUpdate: BoardUpdate) {
    const seenSquares = new Set<Square>();
    for (const squareFacts of boardUpdate.facts.squares.values()) {
      const depositFacts = squareFacts.deposit;
      if (!depositFacts) continue;

      const resource = depositFacts.dominant.material;

      const square = squareFacts.square;
      seenSquares.add(square);
      const existing = this.resourceDepositBySquare.get(square);
      if (existing?.resource === resource) {
        existing.setFraction(depositFacts.fraction);
        continue;
      }

      if (existing) this.despawn(square, existing);
      this.spawn(resource, square, depositFacts.fraction);
    }

    for (const [square, resourceDeposit] of [...this.resourceDepositBySquare]) {
      if (!seenSquares.has(square)) this.despawn(square, resourceDeposit);
    }
  }

  isBoardUpdateFinished() {
    return true;
  }

  dispose() {
    for (const [square, resourceDeposit] of [...this.resourceDepositBySquare])
      this.despawn(square, resourceDeposit);
  }

  private spawn(resource: ResourceId, square: Square, fraction: number) {
    const resourceDeposit = new ResourceDeposit(resource, square, fraction);
    this.world.add(resourceDeposit);
    this.resourceDepositBySquare.set(square, resourceDeposit);
  }

  private despawn(square: Square, resourceDeposit: ResourceDeposit) {
    this.world.removeAndDispose(resourceDeposit);
    this.resourceDepositBySquare.delete(square);
  }
}
